import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, ArrowLeft } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function WhatsAppLoginDialog({ open, onOpenChange, onSuccess }) {
  const { checkAuth } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("phone"); setPhone(""); setCode(""); setBusy(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const sendOtp = async () => {
    const digits = phone.replace(/[^0-9]/g, "");
    if (digits.length !== 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setBusy(true);
    try {
      await api.post("/auth/whatsapp/send-otp", { phone: digits });
      setStep("otp");
      toast.success("We sent a 6-digit code to your WhatsApp.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code. Please try again.");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    const c = code.replace(/[^0-9]/g, "");
    if (c.length < 4) { toast.error("Enter the code from WhatsApp."); return; }
    setBusy(true);
    try {
      await api.post("/auth/whatsapp/verify-otp", { phone: phone.replace(/[^0-9]/g, ""), code: c });
      await checkAuth();
      toast.success("Signed in with WhatsApp!");
      close(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="whatsapp-login-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" /> Sign in with WhatsApp
          </DialogTitle>
          <DialogDescription>
            {step === "phone"
              ? "Enter your WhatsApp mobile number — we'll send a one-time code."
              : `Enter the 6-digit code we sent to +91 ${phone.replace(/[^0-9]/g, "")}.`}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Mobile number</Label>
              <div className="flex gap-2">
                <span className="inline-flex items-center px-3 rounded-md border border-input bg-muted text-sm font-mono-num">+91</span>
                <Input
                  data-testid="wa-phone-input"
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                  onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                  placeholder="10-digit number"
                  className="flex-1"
                />
              </div>
            </div>
            <Button data-testid="wa-send-otp-btn" onClick={sendOtp} disabled={busy}
              className="w-full rounded-full font-semibold bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              {busy ? "Sending…" : "Send code on WhatsApp"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              By continuing you agree to our Terms &amp; Conditions.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>WhatsApp code</Label>
              <Input
                data-testid="wa-otp-input"
                inputMode="numeric"
                maxLength={8}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
                placeholder="Enter 6-digit code"
                className="text-center text-lg tracking-[0.4em] font-mono-num"
              />
            </div>
            <Button data-testid="wa-verify-otp-btn" onClick={verifyOtp} disabled={busy}
              className="w-full rounded-full font-semibold bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {busy ? "Verifying…" : "Verify & sign in"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button data-testid="wa-back-btn" onClick={() => setStep("phone")} className="text-muted-foreground hover:text-foreground flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Change number
              </button>
              <button data-testid="wa-resend-btn" onClick={sendOtp} disabled={busy} className="text-primary hover:opacity-80">
                Resend code
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
